/**
 * EVA Tier — Phase 4 foundation JS module, new file, 2026-08-04.
 *
 * Single shared client-side module for anything that needs to know "what are the tiers" or "which
 * tier is this customer" — the cart drawer, member-price badges, and the membership page all load
 * this instead of each re-declaring their own copy of tag-matching/config-parsing helpers, which is
 * exactly what the existing cart-drawer.liquid already does TWICE internally (two separate <script>
 * blocks, byte-for-byte duplicate getCustomerTags()) and what ~19 files across the theme do in
 * their own variations. New tier-aware files should use window.EvaTier instead of writing their own.
 *
 * Reads config from #EvaTierConfig (see snippets/eva-tier-config.liquid) — the ONE place the
 * elite_pricing:public_pricing_config metafield gets parsed. Loaded globally via one line added to
 * layout/theme.liquid, before any script that calls EvaTier.*.
 */
(function () {
  "use strict";

  let cachedConfig = null;

  function getConfig() {
    if (cachedConfig) return cachedConfig;
    const node = document.getElementById("EvaTierConfig");
    if (!node) {
      cachedConfig = { tiers: {} };
      return cachedConfig;
    }
    try {
      const parsed = JSON.parse(node.textContent || "{}");
      cachedConfig = parsed && parsed.tiers ? parsed : { tiers: {} };
    } catch (e) {
      cachedConfig = { tiers: {} };
    }
    return cachedConfig;
  }

  function getTiers() {
    return getConfig().tiers || {};
  }

  // Returns tiers as an array sorted by rank ascending (Legacy -> Elite -> Elite Plus), each with
  // its `key` attached — the ordering product/comparison UIs actually want to render in.
  function getTiersSorted() {
    const tiers = getTiers();
    return Object.keys(tiers)
      .map(function (key) {
        return Object.assign({ key: key }, tiers[key]);
      })
      .sort(function (a, b) {
        return a.rank - b.rank;
      });
  }

  function getTier(tierKey) {
    return getTiers()[tierKey] || null;
  }

  /**
   * Rank-based highest-tier-wins resolution — the exact same algorithm the pricing Function uses
   * server-side (see eva-tier-membership's tierResolution.server.ts / the Rust Function), so a
   * customer with multiple tags (e.g. mid-upgrade) resolves to the same tier here as at checkout.
   * customerTags should already be lowercased; pass raw Liquid `customer.tags` output as-is if it
   * was rendered with the `| downcase` filter (see how eva-tier-config.liquid's callers render it).
   */
  function resolveTierKey(customerTags) {
    if (!Array.isArray(customerTags) || customerTags.length === 0) return null;
    const lowerCustomerTags = customerTags.map(function (t) {
      return String(t || "").trim().toLowerCase();
    });

    let best = null;
    const tiers = getTiersSorted();
    for (let i = 0; i < tiers.length; i++) {
      const tier = tiers[i];
      const tierTags = Array.isArray(tier.tags) ? tier.tags : [];
      const matches = tierTags.some(function (tag) {
        return lowerCustomerTags.indexOf(String(tag).toLowerCase()) !== -1;
      });
      if (matches && (!best || tier.rank > best.rank)) {
        best = tier;
      }
    }
    return best ? best.key : null;
  }

  function isMember(customerTags) {
    return resolveTierKey(customerTags) !== null;
  }

  // cents -> "$19.95". AUD-only store — matches the existing (independently duplicated in
  // cart-drawer.liquid and elite-membership-cart.liquid) en-AU Intl.NumberFormat convention.
  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return "$" + amount.toFixed(2);
    }
  }

  window.EvaTier = {
    getConfig: getConfig,
    getTiers: getTiers,
    getTiersSorted: getTiersSorted,
    getTier: getTier,
    resolveTierKey: resolveTierKey,
    isMember: isMember,
    formatMoney: formatMoney,
  };
})();
